import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BookOpen, Check, ChevronDown, Search } from 'lucide-react';
import './SearchableFormationSelect.css';

const normalize = (value) => String(value || '').toLowerCase().trim();

const SearchableFormationSelect = ({
  formations = [],
  value,
  onChange,
  placeholder,
  disabled = false,
  inputId = 'formation',
}) => {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selectedFormation = useMemo(
    () => formations.find((formation) => String(formation.id) === String(value)),
    [formations, value]
  );

  const filteredFormations = useMemo(() => {
    const normalizedQuery = normalize(query);
    if (!normalizedQuery) return formations;

    return formations.filter((formation) => {
      const searchableText = [
        formation.title,
        formation.description,
        formation.centre_name,
      ].join(' ');
      return normalize(searchableText).includes(normalizedQuery);
    });
  }, [formations, query]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayValue = isOpen ? query : (selectedFormation?.title || '');

  const handleFocus = () => {
    if (disabled) return;
    setIsOpen(true);
    setQuery('');
  };

  const handleSelect = (formationId) => {
    onChange(String(formationId));
    setIsOpen(false);
    setQuery('');
  };

  return (
    <div className="searchable-formation-select" ref={containerRef}>
      <div className="searchable-formation-input-wrap">
        <Search size={18} className="searchable-formation-search-icon" />
        <input
          id={inputId}
          type="text"
          value={displayValue}
          onFocus={handleFocus}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
          }}
          placeholder={placeholder}
          className="form-input ui-input searchable-formation-input"
          disabled={disabled}
          autoComplete="off"
        />
        <ChevronDown size={18} className="searchable-formation-chevron" />
      </div>

      {isOpen && !disabled && (
        <div className="searchable-formation-menu">
          {filteredFormations.length === 0 ? (
            <div className="searchable-formation-empty">
              <BookOpen size={18} />
              <span>Aucune formation trouvée</span>
            </div>
          ) : (
            filteredFormations.map((formation) => {
              const selected = String(formation.id) === String(value);
              return (
                <button
                  type="button"
                  key={formation.id}
                  className={`searchable-formation-option ${selected ? 'selected' : ''}`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    handleSelect(formation.id);
                  }}
                >
                  <span className="searchable-formation-option-text">
                    <span className="searchable-formation-title">{formation.title}</span>
                    {formation.centre_name && (
                      <span className="searchable-formation-centre">{formation.centre_name}</span>
                    )}
                  </span>
                  {selected && <Check size={16} />}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableFormationSelect;
